export const mediquestSessionItems = [
	{
		type: "SET",
		stimulus: {
			id: 100,
			content: {
				instruction:
					"Follow the clinical case and answer the questions that follow.",
				passage: {
					title: "Clinical Case: Pulmonary Embolism Interactive Case",
					paragraphs: [],
				},
			},
			metadata: {
				type: "READING",
				skill: "READING",
				title: "Stimulus",
			},
		},
		items: [
			{
				id: 1,
				versionId: 1,
				content: {
					parentId: 100,
					parentType: "SET",
					stem: "You are the medical student responsible for taking the history of the patient, a 45-year-old man presenting with acute chest pain and shortness of breath. Your objective is to clarify the onset, nature, severity, and possible risk factors for his symptoms through focused questioning.",
					media: [
						{
							id: "vi6v92LSL5Q5PkFBYglLpk8G",
							url: "vi6v92LSL5Q5PkFBYglLpk8G",
							type: "video",
							transcript:
								"My chest hurts when I breathe in and I can’t catch my breath.",
						},
					],
					choices: [],
					instruction: "Type or say your questions one at a time",
				},
				tags: [],
				response: {
					maximumScore: 10,
					subskillsSchema: [
						{
							maxScore: 5,
							name: "History Taking",
							icon: "clipboard-text-outline",
							description:
								"Demonstrates ability to obtain comprehensive and relevant patient information through structured questioning",
						},
						{
							maxScore: 5,
							name: "Professionalism",
							icon: "handshake-outline",
							description:
								"Exhibits appropriate clinical communication skills, empathy, and recognition of case urgency",
						},
					],
					goldStandard: {
						OLDCART: {
							onset: "Sudden this morning",
							location: "Central chest",
							duration: "Constant",
							character: "Sharp/stabbing",
							aggravating: "Worse with deep breath",
							relieving: "None",
							radiation: "No",
							timing: "Continuous",
							severity: "7/10",
						},
						riskFactors: [
							"Recent knee surgery",
							"Peri-operative immobility",
							"Left calf swelling",
						],
					},
				},
				metadata: {
					type: "INTERACTIVE_VIDEO",
					skill: "SPEAKING",
					title: "Interactive Video",
					topics: [],
				},
				score: null,
			},
			{
				id: 2,
				versionId: 1,
				content: {
					parentId: 100,
					parentVersionId: 1,
					parentType: "SET",
					stem: "You have just completed your focused history with the patient, who reports sudden-onset sharp chest pain and shortness of breath that began this morning and has not improved with rest. To further clarify your differential diagnosis, you now decide to perform a review of systems.",
					instruction:
						"Select all additional symptoms you would specifically screen for at this point in your evaluation.",
					choices: [
						{
							id: "aca99229-e95e-4575-adcb-90a1a87b606a",
							stem: "Calf pain/swelling",
							text: "Calf pain/swelling",
							isCorrect: true,
							explanation: `When a patient presents with **acute chest pain and shortness of breath**, a focused and efficient **Review of Systems (ROS)** is essential to rapidly narrow the differential diagnosis and inform urgent management decisions. Not all symptoms contribute equally in this context—some point clearly toward life-threatening conditions, while others offer minimal diagnostic value and can be safely deferred. Prioritizing the most clinically relevant findings allows for faster decision-making and reduces distractions, ultimately improving patient outcomes.\n\nOne of the most critical symptoms to assess is **calf pain or swelling**, particularly in the setting of recent surgery. A patient stating, \"Yes, my left leg has been swollen and sore, especially since the knee surgery,\" should raise immediate concern for **deep vein thrombosis (DVT)**, a condition that can progress to **pulmonary embolism (PE)**—a potentially fatal event that requires prompt intervention. Similarly, **hemoptysis** warrants routine screening. Even when the patient responds, \"No, I haven't been coughing up any blood,\" the question remains valuable, as hemoptysis is a classic **red flag** for PE or malignancy and should not be overlooked.\n\nAnother high-priority symptom is **chest tightness**. When a patient describes it as \"tight and sharp, especially when I breathe in deeply,\" it provides meaningful insight into the nature of the discomfort and helps differentiate among **pleuritic pain**, **cardiac ischemia**, or **musculoskeletal causes**. Lastly, **syncope or near-syncope** merits attention. Even if a patient reports only lightheadedness—\"No, I haven't passed out, but I do feel lightheaded\"—this could reflect **hemodynamic compromise** or a potentially dangerous **arrhythmia**, both of which demand further evaluation.\n\nIn contrast, several symptoms commonly included in general ROS are of **lower diagnostic utility** in this acute setting. **Fever** may indicate infection, but its absence does little to rule in or out critical cardiopulmonary causes. **Cough**, especially when mild and dry, is too **nonspecific** to influence early decision-making. **Palpitations**, while potentially relevant, are often better evaluated through vital signs and ECG than through subjective reports like \"My heart feels like it's racing.\" Similarly, **headache** lacks a direct connection to chest pain and can usually be deprioritized. **Diaphoresis**, though it may accompany serious illness, is too broadly associated with both benign and critical conditions to add immediate diagnostic clarity.\n\n**Key Takeaways**\n\n- Prioritize symptoms that directly influence the chest pain and dyspnea differential.\n- Screen for high-yield symptoms to expedite diagnosis and guide acute management.\n- Red flag symptoms—like calf swelling, hemoptysis, and syncope—often indicate life-threatening conditions.\n- Avoid overemphasizing vague or nonspecific symptoms during initial triage.`,
							numFirstSelections: 83,
							explanation_japanese: "",
							explanation_mandarin: "",
						},
						{
							id: "c56020ac-3839-462e-bb65-60eaabc8573e",
							stem: "Hemoptysis",
							text: "Hemoptysis",
							isCorrect: true,
							explanation: `When a patient presents with **acute chest pain and shortness of breath**, a focused and efficient **Review of Systems (ROS)** is essential to rapidly narrow the differential diagnosis and inform urgent management decisions. Not all symptoms contribute equally in this context—some point clearly toward life-threatening conditions, while others offer minimal diagnostic value and can be safely deferred. Prioritizing the most clinically relevant findings allows for faster decision-making and reduces distractions, ultimately improving patient outcomes.\n\nOne of the most critical symptoms to assess is **calf pain or swelling**, particularly in the setting of recent surgery. A patient stating, \"Yes, my left leg has been swollen and sore, especially since the knee surgery,\" should raise immediate concern for **deep vein thrombosis (DVT)**, a condition that can progress to **pulmonary embolism (PE)**—a potentially fatal event that requires prompt intervention. Similarly, **hemoptysis** warrants routine screening. Even when the patient responds, \"No, I haven't been coughing up any blood,\" the question remains valuable, as hemoptysis is a classic **red flag** for PE or malignancy and should not be overlooked.\n\nAnother high-priority symptom is **chest tightness**. When a patient describes it as \"tight and sharp, especially when I breathe in deeply,\" it provides meaningful insight into the nature of the discomfort and helps differentiate among **pleuritic pain**, **cardiac ischemia**, or **musculoskeletal causes**. Lastly, **syncope or near-syncope** merits attention. Even if a patient reports only lightheadedness—\"No, I haven't passed out, but I do feel lightheaded\"—this could reflect **hemodynamic compromise** or a potentially dangerous **arrhythmia**, both of which demand further evaluation.\n\nIn contrast, several symptoms commonly included in general ROS are of **lower diagnostic utility** in this acute setting. **Fever** may indicate infection, but its absence does little to rule in or out critical cardiopulmonary causes. **Cough**, especially when mild and dry, is too **nonspecific** to influence early decision-making. **Palpitations**, while potentially relevant, are often better evaluated through vital signs and ECG than through subjective reports like \"My heart feels like it's racing.\" Similarly, **headache** lacks a direct connection to chest pain and can usually be deprioritized. **Diaphoresis**, though it may accompany serious illness, is too broadly associated with both benign and critical conditions to add immediate diagnostic clarity.\n\n**Key Takeaways**\n\n- Prioritize symptoms that directly influence the chest pain and dyspnea differential.\n- Screen for high-yield symptoms to expedite diagnosis and guide acute management.\n- Red flag symptoms—like calf swelling, hemoptysis, and syncope—often indicate life-threatening conditions.\n- Avoid overemphasizing vague or nonspecific symptoms during initial triage.`,
							numFirstSelections: 14,
						},
						{
							id: "1baeb02b-12f6-41a8-a7f3-865d9a0553aa",
							stem: "Fever",
							text: "Fever",
							isCorrect: false,
							explanation: null,
							numFirstSelections: 7,
						},
						{
							id: "b7441d0f-143d-4bbc-ba88-f9cd5c9e7858",
							stem: "Cough",
							text: "Cough",
							isCorrect: false,
							explanation: null,
							numFirstSelections: 10,
						},
						{
							id: "f88db623-e2a2-4947-89ea-8121b118c82c",
							stem: "Palpitations",
							text: "Palpitations",
							isCorrect: false,
							explanation: null,
							numFirstSelections: 1,
						},
						{
							id: "acc3184d-08a5-4586-83d1-07eb215cff57",
							stem: "Chest tightness",
							text: "Chest tightness",
							isCorrect: true,
							explanation: `When a patient presents with **acute chest pain and shortness of breath**, a focused and efficient **Review of Systems (ROS)** is essential to rapidly narrow the differential diagnosis and inform urgent management decisions. Not all symptoms contribute equally in this context—some point clearly toward life-threatening conditions, while others offer minimal diagnostic value and can be safely deferred. Prioritizing the most clinically relevant findings allows for faster decision-making and reduces distractions, ultimately improving patient outcomes.\n\nOne of the most critical symptoms to assess is **calf pain or swelling**, particularly in the setting of recent surgery. A patient stating, \"Yes, my left leg has been swollen and sore, especially since the knee surgery,\" should raise immediate concern for **deep vein thrombosis (DVT)**, a condition that can progress to **pulmonary embolism (PE)**—a potentially fatal event that requires prompt intervention. Similarly, **hemoptysis** warrants routine screening. Even when the patient responds, \"No, I haven't been coughing up any blood,\" the question remains valuable, as hemoptysis is a classic **red flag** for PE or malignancy and should not be overlooked.\n\nAnother high-priority symptom is **chest tightness**. When a patient describes it as \"tight and sharp, especially when I breathe in deeply,\" it provides meaningful insight into the nature of the discomfort and helps differentiate among **pleuritic pain**, **cardiac ischemia**, or **musculoskeletal causes**. Lastly, **syncope or near-syncope** merits attention. Even if a patient reports only lightheadedness—\"No, I haven't passed out, but I do feel lightheaded\"—this could reflect **hemodynamic compromise** or a potentially dangerous **arrhythmia**, both of which demand further evaluation.\n\nIn contrast, several symptoms commonly included in general ROS are of **lower diagnostic utility** in this acute setting. **Fever** may indicate infection, but its absence does little to rule in or out critical cardiopulmonary causes. **Cough**, especially when mild and dry, is too **nonspecific** to influence early decision-making. **Palpitations**, while potentially relevant, are often better evaluated through vital signs and ECG than through subjective reports like \"My heart feels like it's racing.\" Similarly, **headache** lacks a direct connection to chest pain and can usually be deprioritized. **Diaphoresis**, though it may accompany serious illness, is too broadly associated with both benign and critical conditions to add immediate diagnostic clarity.\n\n**Key Takeaways**\n\n- Prioritize symptoms that directly influence the chest pain and dyspnea differential.\n- Screen for high-yield symptoms to expedite diagnosis and guide acute management.\n- Red flag symptoms—like calf swelling, hemoptysis, and syncope—often indicate life-threatening conditions.\n- Avoid overemphasizing vague or nonspecific symptoms during initial triage.`,
							numFirstSelections: 14,
							explanation_japanese: "",
							explanation_mandarin: "",
						},
						{
							id: "d7e8f9a0-b1c2-3d4e-5f6g-7h8i9j0k1l2m",
							stem: "Syncope",
							text: "Syncope",
							isCorrect: true,
							explanation: `When a patient presents with **acute chest pain and shortness of breath**, a focused and efficient **Review of Systems (ROS)** is essential to rapidly narrow the differential diagnosis and inform urgent management decisions. Not all symptoms contribute equally in this context—some point clearly toward life-threatening conditions, while others offer minimal diagnostic value and can be safely deferred. Prioritizing the most clinically relevant findings allows for faster decision-making and reduces distractions, ultimately improving patient outcomes.\n\nOne of the most critical symptoms to assess is **calf pain or swelling**, particularly in the setting of recent surgery. A patient stating, \"Yes, my left leg has been swollen and sore, especially since the knee surgery,\" should raise immediate concern for **deep vein thrombosis (DVT)**, a condition that can progress to **pulmonary embolism (PE)**—a potentially fatal event that requires prompt intervention. Similarly, **hemoptysis** warrants routine screening. Even when the patient responds, \"No, I haven't been coughing up any blood,\" the question remains valuable, as hemoptysis is a classic **red flag** for PE or malignancy and should not be overlooked.\n\nAnother high-priority symptom is **chest tightness**. When a patient describes it as \"tight and sharp, especially when I breathe in deeply,\" it provides meaningful insight into the nature of the discomfort and helps differentiate among **pleuritic pain**, **cardiac ischemia**, or **musculoskeletal causes**. Lastly, **syncope or near-syncope** merits attention. Even if a patient reports only lightheadedness—\"No, I haven't passed out, but I do feel lightheaded\"—this could reflect **hemodynamic compromise** or a potentially dangerous **arrhythmia**, both of which demand further evaluation.\n\nIn contrast, several symptoms commonly included in general ROS are of **lower diagnostic utility** in this acute setting. **Fever** may indicate infection, but its absence does little to rule in or out critical cardiopulmonary causes. **Cough**, especially when mild and dry, is too **nonspecific** to influence early decision-making. **Palpitations**, while potentially relevant, are often better evaluated through vital signs and ECG than through subjective reports like \"My heart feels like it's racing.\" Similarly, **headache** lacks a direct connection to chest pain and can usually be deprioritized. **Diaphoresis**, though it may accompany serious illness, is too broadly associated with both benign and critical conditions to add immediate diagnostic clarity.\n\n**Key Takeaways**\n\n- Prioritize symptoms that directly influence the chest pain and dyspnea differential.\n- Screen for high-yield symptoms to expedite diagnosis and guide acute management.\n- Red flag symptoms—like calf swelling, hemoptysis, and syncope—often indicate life-threatening conditions.\n- Avoid overemphasizing vague or nonspecific symptoms during initial triage.`,
							numFirstSelections: 12,
							explanation_japanese: "",
							explanation_mandarin: "",
						},
						{
							id: "n3o4p5q6-r7s8-t9u0-v1w2-x3y4z5a6b7c8",
							stem: "Headache",
							text: "Headache",
							isCorrect: false,
							explanation: null,
							numFirstSelections: 5,
						},
						{
							id: "d9e0f1g2-h3i4-j5k6-l7m8-n9o0p1q2r3s4",
							stem: "Diaphoresis",
							text: "Diaphoresis",
							isCorrect: false,
							explanation: null,
							numFirstSelections: 3,
						},
					],
					media: null,
				},
				tags: [
					{
						id: 1146,
						type: "Skill",
						label: "Reading",
						breadcrumb: "Reading",
					},
				],
				response: {
					maximumScore: 2,
					goldStandard: {
						ROS: [
							"Calf pain/swelling",
							"Hemoptysis",
							"Syncope",
							"Palpitations",
							"Chest tightness",
						],
					},
					correctResponse: [
						{
							id: "aca99229-e95e-4575-adcb-90a1a87b606a",
						},
						{
							id: "c56020ac-3839-462e-bb65-60eaabc8573e",
						},
						{
							id: "acc3184d-08a5-4586-83d1-07eb215cff57",
						},
						{
							id: "d7e8f9a0-b1c2-3d4e-5f6g-7h8i9j0k1l2m",
						},
					],
					numChoicesRequired: {
						max: 9,
						min: 9,
					},
				},
				metadata: {
					type: "MATCH_MS",
					skill: "READING",
					title: "Match Multiple Selection",
					topics: ["Prose Summary"],
				},
				score: null,
			},
			{
				id: 3,
				versionId: 1,
				content: {
					parentId: 100,
					parentVersionId: 1,
					parentType: "SET",
					stem: "You have completed your review of systems for the patient. On review of systems, you learned he feels lightheaded but has not fainted, describes his chest as both tight and sharp, and denies fever or coughing up blood. You are now ready to perform a focused physical examination to gather more information and further narrow your differential diagnosis.",
					instruction:
						"Select essential physical exam maneuvers that will help identify the most likely causes and rule out life-threatening conditions",
					choices: [
						{
							id: "7f8e9d0c-1b2a-3c4d-5e6f-7g8h9i0j1k2l",
							stem: "Cardiac auscultation",
							text: "Cardiac auscultation",
							isCorrect: true,
							explanation:
								"When performing a physical examination on a patient with **sudden sharp chest pain**, **shortness of breath**, and a history of **recent left leg swelling following knee surgery**, it is crucial to focus on exam components most likely to yield actionable findings. The goal is to rapidly assess for signs of **deep vein thrombosis (DVT)** or **pulmonary embolism (PE)**—both of which can present with this combination of symptoms—and to prioritize efficiency in a potentially emergent setting.\n\n**Cardiac auscultation** should be one of the first maneuvers performed. Hearing **tachycardia with a regular rhythm** indicates the body is under physiologic stress, possibly from hypoxia or hemodynamic strain. This finding is common in patients with PE and supports further investigation. Equally important is **lung auscultation**, which may reveal subtle but critical abnormalities. In this case, while most lung fields are clear, **faint crackles at the right base** suggest the possibility of a small pulmonary infarct or effusion—findings consistent with embolic disease.\n\nA targeted **inspection of the legs** is highly valuable. Visual asymmetry, such as a **swollen and red left calf**, supports the clinical suspicion of a DVT as the potential source of embolic symptoms. Following visual inspection, **palpation of the calves** may reveal **tenderness**, further reinforcing concern for thrombosis. These lower extremity findings are especially significant given the patient's **recent orthopedic surgery**—a well-known risk factor for thromboembolic complications.\n\nIn contrast, other routine physical exam maneuvers offer minimal benefit in this acute scenario and can be safely deferred unless specifically indicated. An **abdominal exam**, though commonly performed, is unlikely to provide useful information in the absence of abdominal symptoms. Similarly, assessing **jugular venous pressure (JVP)** is of limited value here; while helpful in overt heart failure, it typically remains normal in isolated PE and would not change immediate management. A **neurological exam** can also be omitted unless the patient has focal symptoms or altered mental status. Lastly, an **oropharyngeal exam** offers no relevant findings unless there are airway complaints, and thus it does not need to be included in the initial assessment.\n\n**Key Takeaways**\n\n- **Prioritize heart, lung, and lower extremity exams**.\n- **Positive findings—tachycardia, abnormal pulmonary sounds, and unilateral leg changes—serve as red flags** guiding further urgent evaluation.\n- **Omitting unrelated exams (abdomen, neuro, oropharynx, JVP)** enables efficient, high-yield assessment.",
							numFirstSelections: 83,
							explanation_japanese: "",
							explanation_mandarin: "",
						},
						{
							id: "a1b2c3d4-e5f6-4g7h-8i9j-0k1l2m3n4o5p",
							stem: "Lung auscultation",
							text: "Lung auscultation",
							isCorrect: true,
							explanation:
								"When performing a physical examination on a patient with **sudden sharp chest pain**, **shortness of breath**, and a history of **recent left leg swelling following knee surgery**, it is crucial to focus on exam components most likely to yield actionable findings. The goal is to rapidly assess for signs of **deep vein thrombosis (DVT)** or **pulmonary embolism (PE)**—both of which can present with this combination of symptoms—and to prioritize efficiency in a potentially emergent setting.\n\n**Cardiac auscultation** should be one of the first maneuvers performed. Hearing **tachycardia with a regular rhythm** indicates the body is under physiologic stress, possibly from hypoxia or hemodynamic strain. This finding is common in patients with PE and supports further investigation. Equally important is **lung auscultation**, which may reveal subtle but critical abnormalities. In this case, while most lung fields are clear, **faint crackles at the right base** suggest the possibility of a small pulmonary infarct or effusion—findings consistent with embolic disease.\n\nA targeted **inspection of the legs** is highly valuable. Visual asymmetry, such as a **swollen and red left calf**, supports the clinical suspicion of a DVT as the potential source of embolic symptoms. Following visual inspection, **palpation of the calves** may reveal **tenderness**, further reinforcing concern for thrombosis. These lower extremity findings are especially significant given the patient's **recent orthopedic surgery**—a well-known risk factor for thromboembolic complications.\n\nIn contrast, other routine physical exam maneuvers offer minimal benefit in this acute scenario and can be safely deferred unless specifically indicated. An **abdominal exam**, though commonly performed, is unlikely to provide useful information in the absence of abdominal symptoms. Similarly, assessing **jugular venous pressure (JVP)** is of limited value here; while helpful in overt heart failure, it typically remains normal in isolated PE and would not change immediate management. A **neurological exam** can also be omitted unless the patient has focal symptoms or altered mental status. Lastly, an **oropharyngeal exam** offers no relevant findings unless there are airway complaints, and thus it does not need to be included in the initial assessment.\n\n**Key Takeaways**\n\n- **Prioritize heart, lung, and lower extremity exams**.\n- **Positive findings—tachycardia, abnormal pulmonary sounds, and unilateral leg changes—serve as red flags** guiding further urgent evaluation.\n- **Omitting unrelated exams (abdomen, neuro, oropharynx, JVP)** enables efficient, high-yield assessment.",
							numFirstSelections: 14,
						},
						{
							id: "q1w2e3r4-t5y6-u7i8-o9p0-a1s2d3f4g5h6",
							stem: "Inspect legs",
							text: "Inspect legs",
							isCorrect: true,
							explanation:
								"When performing a physical examination on a patient with **sudden sharp chest pain**, **shortness of breath**, and a history of **recent left leg swelling following knee surgery**, it is crucial to focus on exam components most likely to yield actionable findings. The goal is to rapidly assess for signs of **deep vein thrombosis (DVT)** or **pulmonary embolism (PE)**—both of which can present with this combination of symptoms—and to prioritize efficiency in a potentially emergent setting.\n\n**Cardiac auscultation** should be one of the first maneuvers performed. Hearing **tachycardia with a regular rhythm** indicates the body is under physiologic stress, possibly from hypoxia or hemodynamic strain. This finding is common in patients with PE and supports further investigation. Equally important is **lung auscultation**, which may reveal subtle but critical abnormalities. In this case, while most lung fields are clear, **faint crackles at the right base** suggest the possibility of a small pulmonary infarct or effusion—findings consistent with embolic disease.\n\nA targeted **inspection of the legs** is highly valuable. Visual asymmetry, such as a **swollen and red left calf**, supports the clinical suspicion of a DVT as the potential source of embolic symptoms. Following visual inspection, **palpation of the calves** may reveal **tenderness**, further reinforcing concern for thrombosis. These lower extremity findings are especially significant given the patient's **recent orthopedic surgery**—a well-known risk factor for thromboembolic complications.\n\nIn contrast, other routine physical exam maneuvers offer minimal benefit in this acute scenario and can be safely deferred unless specifically indicated. An **abdominal exam**, though commonly performed, is unlikely to provide useful information in the absence of abdominal symptoms. Similarly, assessing **jugular venous pressure (JVP)** is of limited value here; while helpful in overt heart failure, it typically remains normal in isolated PE and would not change immediate management. A **neurological exam** can also be omitted unless the patient has focal symptoms or altered mental status. Lastly, an **oropharyngeal exam** offers no relevant findings unless there are airway complaints, and thus it does not need to be included in the initial assessment.\n\n**Key Takeaways**\n\n- **Prioritize heart, lung, and lower extremity exams**.\n- **Positive findings—tachycardia, abnormal pulmonary sounds, and unilateral leg changes—serve as red flags** guiding further urgent evaluation.\n- **Omitting unrelated exams (abdomen, neuro, oropharynx, JVP)** enables efficient, high-yield assessment.",
							numFirstSelections: 7,
						},
						{
							id: "j1k2l3m4-n5b6-v7c8-x9z0-q1w2e3r4t5y6",
							stem: "Palpate calves",
							text: "Palpate calves",
							isCorrect: true,
							explanation:
								"When performing a physical examination on a patient with **sudden sharp chest pain**, **shortness of breath**, and a history of **recent left leg swelling following knee surgery**, it is crucial to focus on exam components most likely to yield actionable findings. The goal is to rapidly assess for signs of **deep vein thrombosis (DVT)** or **pulmonary embolism (PE)**—both of which can present with this combination of symptoms—and to prioritize efficiency in a potentially emergent setting.\n\n**Cardiac auscultation** should be one of the first maneuvers performed. Hearing **tachycardia with a regular rhythm** indicates the body is under physiologic stress, possibly from hypoxia or hemodynamic strain. This finding is common in patients with PE and supports further investigation. Equally important is **lung auscultation**, which may reveal subtle but critical abnormalities. In this case, while most lung fields are clear, **faint crackles at the right base** suggest the possibility of a small pulmonary infarct or effusion—findings consistent with embolic disease.\n\nA targeted **inspection of the legs** is highly valuable. Visual asymmetry, such as a **swollen and red left calf**, supports the clinical suspicion of a DVT as the potential source of embolic symptoms. Following visual inspection, **palpation of the calves** may reveal **tenderness**, further reinforcing concern for thrombosis. These lower extremity findings are especially significant given the patient's **recent orthopedic surgery**—a well-known risk factor for thromboembolic complications.\n\nIn contrast, other routine physical exam maneuvers offer minimal benefit in this acute scenario and can be safely deferred unless specifically indicated. An **abdominal exam**, though commonly performed, is unlikely to provide useful information in the absence of abdominal symptoms. Similarly, assessing **jugular venous pressure (JVP)** is of limited value here; while helpful in overt heart failure, it typically remains normal in isolated PE and would not change immediate management. A **neurological exam** can also be omitted unless the patient has focal symptoms or altered mental status. Lastly, an **oropharyngeal exam** offers no relevant findings unless there are airway complaints, and thus it does not need to be included in the initial assessment.\n\n**Key Takeaways**\n\n- **Prioritize heart, lung, and lower extremity exams**.\n- **Positive findings—tachycardia, abnormal pulmonary sounds, and unilateral leg changes—serve as red flags** guiding further urgent evaluation.\n- **Omitting unrelated exams (abdomen, neuro, oropharynx, JVP)** enables efficient, high-yield assessment.",
							numFirstSelections: 10,
						},
						{
							id: "u1i2o3p4-a5s6-d7f8-g9h0-j1k2l3m4n5b6",
							stem: "Abdominal exam",
							text: "Abdominal exam",
							isCorrect: false,
							explanation: null,
							numFirstSelections: 1,
						},
						{
							id: "v1c2x3z4-q5w6-e7r8-t9y0-u1i2o3p4a5s6",
							stem: "Jugular venous pressure (JVP)",
							text: "Jugular venous pressure (JVP)",
							isCorrect: false,
							explanation: null,
							numFirstSelections: 14,
							explanation_japanese: "",
							explanation_mandarin: "",
						},
						{
							id: "d1f2g3h4-j5k6-l7m8-n9b0-v1c2x3z4q5w6",
							stem: "Neurological exam",
							text: "Neurological exam",
							isCorrect: false,
							explanation: null,
							numFirstSelections: 12,
							explanation_japanese: "",
							explanation_mandarin: "",
						},
						{
							id: "e1r2t3y4-u5i6-o7p8-a9s0-d1f2g3h4j5k6",
							stem: "Oropharyngeal exam",
							text: "Oropharyngeal exam",
							isCorrect: false,
							explanation: null,
							numFirstSelections: 5,
						},
					],
					media: null,
				},
				tags: [
					{
						id: 1146,
						type: "Skill",
						label: "Reading",
						breadcrumb: "Reading",
					},
				],
				response: {
					maximumScore: 2,
					goldStandard: {
						physicalExam: [
							"Clear/faint crackles (right base)",
							"Tachycardia, regular rhythm",
							"Left calf swollen, erythematous, tender",
						],
					},
					correctResponse: [
						{
							id: "7f8e9d0c-1b2a-3c4d-5e6f-7g8h9i0j1k2l",
						},
						{
							id: "a1b2c3d4-e5f6-4g7h-8i9j-0k1l2m3n4o5p",
						},
						{
							id: "q1w2e3r4-t5y6-u7i8-o9p0-a1s2d3f4g5h6",
						},
						{
							id: "j1k2l3m4-n5b6-v7c8-x9z0-q1w2e3r4t5y6",
						},
					],
					numChoicesRequired: {
						max: 8,
						min: 8,
					},
				},
				metadata: {
					type: "MATCH_MS",
					skill: "READING",
					title: "Match Multiple Selection",
					topics: ["Prose Summary"],
				},
				score: null,
			},
			{
				id: 4,
				type: "AUDIO_INPUT",
				content: {
					parentId: 100,
					parentType: "SET",
					stem: "After your physical exam, you find that the patient is tachycardic with a regular rhythm, has a swollen, red, and tender left calf, and faint crackles are audible at the right lung base. You can now listen to his breath sounds using the audio below.",
					choices: [],
					instruction: `- Listen to the provided audio
- List your top three differential diagnoses in order of likelihood
- Briefly justify your reasoning for each diagnosis based on the findings`,
					media: [
						{
							url: "https://ajxjsnuynuxigljdrsyk.supabase.co/storage/v1/object/public/memorang-assets/demo/nbme/friction_rub.mp3",
							name: "VC627499.a02",
							type: "audio",
							transcript: "Breathing sounds",
						},
					],
				},
				tags: [
					{
						id: 6481,
						type: "Skill",
						label: "Speaking",
						breadcrumb: "Speaking",
					},
				],
				response: {
					maximumScore: 15,
					maximumDuration: 40,

					subskillsSchema: [
						{
							name: "Diagnosis Selection & Ranking",
							icon: "clipboard-list",
							description:
								"Correctly identifies and ranks the most likely and relevant diagnoses",
							maxScore: 5,
						},
						{
							name: "Clinical Justification",
							icon: "stethoscope",
							description:
								"Clearly justifies each diagnosis with evidence from case findings",
							maxScore: 5,
						},
						{
							name: "Relevance & Focus",
							icon: "target",
							description:
								"Avoids implausible/unrelated diagnoses; focuses on supported possibilities",
							maxScore: 5,
						},
					],
				},
				metadata: {
					type: "AUDIO_INPUT",
					skill: "SPEAKING",
					title: "Audio Input Item",
					topics: ["LS Summary"],
				},
				score: 0,
			},
			{
				id: 5,
				type: "MCOS",
				content: {
					parentId: 100,
					parentType: "SET",
					stem: "Based on your suspicion of pulmonary embolism, arrange the following initial management steps for the patient in the order you would perform them.",
					choices: [
						{
							id: "b2c3d4e5-f6a7-5b6c-9d8e-0f1a2b3c4d5e",
							stem: "Oxygen",
							text: "Oxygen",
							media: [],
							isCorrect: false,
							explanation: null,
							numFirstSelections: 53,
						},
						{
							id: "a1b2c3d4-e5f6-4a5b-8c7d-9e0f1a2b3c4d",
							stem: "ABCs",
							text: "ABCs",
							media: [],
							isCorrect: false,
							explanation: null,
							numFirstSelections: 31,
						},
						{
							id: "c3d4e5f6-a7b8-6c7d-0e9f-1a2b3c4d5e6f",
							stem: "IV access/labs",
							text: "IV access/labs",
							media: [],
							isCorrect: false,
							explanation: null,
							numFirstSelections: 35,
						},
						{
							id: "e5f6a7b8-c9d0-8e9f-2a1b-3c4d5e6f7a8b",
							stem: "CT pulmonary angiogram",
							text: "CT pulmonary angiogram",
							media: [],
							isCorrect: false,
							explanation: null,
							numFirstSelections: 45,
						},
						{
							id: "d4e5f6a7-b8c9-7d8e-1f0a-2b3c4d5e6f7a",
							stem: "ECG/CXR",
							text: "ECG/CXR",
							media: [],
							isCorrect: false,
							explanation: null,
							numFirstSelections: 63,
						},
						{
							id: "f6a7b8c9-d0e1-9f0a-3b2c-4d5e6f7a8b9c",
							stem: "Anticoagulation",
							text: "Anticoagulation",
							media: [],
							isCorrect: true,
							explanation:
								"Initial management of suspected **pulmonary embolism** should always follow the emergency principle of stabilizing life threats first, confirming the diagnosis second, and starting definitive treatment last.\n\nThe correct order is:\n1. **Airway, breathing, and circulation (ABCs)**\n2. **Oxygen**\n3. **Intravenous access and laboratory tests**\n4. **Electrocardiogram and chest X-ray**\n5. **Computed tomography (CT) pulmonary angiogram**\n6. **Anticoagulation**\n\nThis sequence is best because early stabilization with airway, breathing, circulation, oxygen, and intravenous access prevents the patient from getting worse during transport or imaging. Bedside tests like electrocardiogram and chest X-ray quickly help rule out other causes. CT pulmonary angiogram provides confirmation of the diagnosis. Anticoagulation should be started as soon as the diagnosis is confirmed, or earlier if the patient is unstable and pulmonary embolism is strongly suspected.\n\nIf you perform steps out of order, such as getting a CT scan before establishing intravenous access, or starting anticoagulation before confirming the diagnosis in a stable patient, you may increase the risk of complications.\n\n**Key Takeaways**\n- Always follow the order: resuscitation, then diagnosis, then treatment.\n- Do not delay oxygen or intravenous access in patients who are unstable.\n- Start anticoagulation once pulmonary embolism is confirmed, or right away if suspicion is high and there are no contraindications.",
							numFirstSelections: 28,
							explanation_japanese: "",
							explanation_mandarin: "",
						},
					],
					instruction: "Drag and drop each step into the correct sequence.",
					highlights: null,
					media: null,
				},
				tags: [
					{
						id: 6479,
						type: "Skill",
						label: "Reading",
						breadcrumb: "Reading",
					},
				],
				response: {
					maximumScore: 1,
					correctResponse: [
						{
							id: "a1b2c3d4-e5f6-4a5b-8c7d-9e0f1a2b3c4d", // ABCs
						},
						{
							id: "b2c3d4e5-f6a7-5b6c-9d8e-0f1a2b3c4d5e", // Oxygen
						},
						{
							id: "c3d4e5f6-a7b8-6c7d-0e9f-1a2b3c4d5e6f", // IV access/labs
						},
						{
							id: "d4e5f6a7-b8c9-7d8e-1f0a-2b3c4d5e6f7a", // ECG/CXR
						},
						{
							id: "e5f6a7b8-c9d0-8e9f-2a1b-3c4d5e6f7a8b", // CT pulmonary angiogram
						},
						{
							id: "f6a7b8c9-d0e1-9f0a-3b2c-4d5e6f7a8b9c", // Anticoagulation
						},
					],
					numChoicesRequired: {
						max: 1,
						min: 1,
					},
				},
				metadata: {
					type: "MCOS",
					skill: "READING",
					title: "Sequence of Order",
					topics: ["Vocabulary"],
				},
				score: 0,
			},
			{
				id: 6,
				type: "WORKFLOW_GRAPH",
				content: {
					parentId: 100,
					parentType: "SET",
					stem: "Given the patient’s presentations and initial findings, you have a high suspicion for pulmonary embolism. Review the clinical workflow below and identify the correct sequence of management steps.",
					instruction: "Correct any mistakes in the diagram below",
					media: null,
				},
				tags: [
					{
						id: 6481,
						type: "Skill",
						label: "Speaking",
						breadcrumb: "Speaking",
					},
				],
				response: {
					maximumScore: 20,
					maximumDuration: 40,
					subskillsSchema: [
						{
							name: "Decision-Making",
							icon: "source-branch",
							description:
								"Correctly identifies and labels the 'Stable' and 'Unstable' branches at the decision node. Labels both branches accurately with appropriate clinical terminology.",
							maxScore: 5,
						},
						{
							name: "Diagnostic and Therapeutic Steps",
							icon: "format-list-numbered",
							description:
								"Accurately orders steps for stable and unstable PE, including appropriate timing of therapeutic interventions and diagnostic workup.",
							maxScore: 5,
						},
						{
							name: "Intervention Selection",
							icon: "medical-bag",
							description:
								"Selects and places correct interventions including empiric anticoagulation, imaging studies, thrombolysis, and therapeutic anticoagulation based on clinical scenario.",
							maxScore: 5,
						},
						{
							name: "Labeling and Communication",
							icon: "label-outline",
							description:
								"Creates clearly labeled, well-organized diagram using standard clinical terminology and nomenclature.",
							maxScore: 5,
						},
					],
				},
				metadata: {
					type: "WORKFLOW_GRAPH",
					skill: "WORKFLOW",
					title: "Workgflow Item",
					topics: ["LS Summary"],
					workflow: {
						initialNodes: [
							{
								id: "A",
								type: "input-node",
								data: {
									label: "Start – Suspected PE",
								},
								position: { x: 0, y: 0 },
							},
							{
								id: "B",
								type: "default-node",
								data: { label: "Stabilize ABCs" },
								position: { x: 0, y: 80 },
							},
							{
								id: "C",
								type: "default-node",
								data: { label: "Give Oxygen, IV Access, Labs" },
								position: { x: 0, y: 160 },
							},
							{
								id: "D",
								type: "decision-node",
								style: { width: 150, height: 150 },
								data: {
									label: "Hemodynamic Status?",
								},
								position: { x: 0, y: 240 },
							},
							{
								id: "U1",
								type: "default-node",
								data: { label: "Empiric Anticoagulation" },
								position: { x: -250, y: 360 },
							},
							{
								id: "U2",
								type: "default-node",
								data: { label: "CT Pulmonary Angiogram" },
								position: { x: -250, y: 440 },
							},
							{
								id: "S1",
								type: "default-node",
								data: { label: "Wells / Geneva Scoring" },
								position: { x: 250, y: 360 },
							},
							{
								id: "S2",
								type: "default-node",
								data: { label: "D-dimer if low–mod probability" },
								position: { x: 250, y: 440 },
							},
							{
								id: "E",
								type: "decision-node",
								style: { width: 150, height: 150 },
								data: {
									label: "Confirmed PE?",
								},
								position: { x: 250, y: 520 },
							},
							{
								id: "S3",
								type: "default-node",
								data: { label: "Thrombolysis / Embolectomy" },
								position: { x: -250, y: 520 },
							},
							{
								id: "T1",
								type: "default-node",
								data: { label: "" },
								position: { x: 100, y: 640 },
							},
							{
								id: "X",
								type: "output-node",
								data: { label: "Consider Alternate Diagnosis" },
								position: { x: 400, y: 640 },
							},
							{
								id: "Z",
								type: "output-node",
								data: { label: "Admit, Monitor, Arrange Follow-Up" },
								position: { x: -120, y: 760 },
							},
						],
						initialEdges: [
							{
								id: "A-B",
								source: "A",
								target: "B",
							},
							{
								id: "B-C",
								source: "B",
								target: "C",
							},
							{
								id: "C-D",
								source: "C",
								target: "D",
							},
							{
								id: "U1-U2",
								source: "U1",
								target: "U2",
							},
							{
								id: "U2-S3",
								source: "U2",
								target: "S3",
							},
							{
								id: "S3-Z",
								source: "S3",
								target: "Z",
							},
							{
								id: "S1-S2",
								source: "S1",
								target: "S2",
							},
							{
								id: "S2-E",
								source: "S2",
								target: "E",
							},
							{
								id: "E-T1",
								source: "E",
								sourceHandle: "positive",
								target: "T1",
								label: "Yes",
								type: "custom",
							},
							{
								id: "E-X",
								source: "E",
								sourceHandle: "negative",
								target: "X",
								label: "No",
								type: "custom",
							},
							{
								id: "T1-Z",
								source: "T1",
								target: "Z",
							},
						],
						correctNodes: [
							{
								id: "A",
								type: "input-node",
								data: {
									label: "Start – Suspected PE",
								},
								position: { x: 0, y: 0 },
							},
							{
								id: "B",
								type: "default-node",
								data: { label: "Stabilize ABCs" },
								position: { x: 0, y: 80 },
							},
							{
								id: "C",
								type: "default-node",
								data: { label: "Give Oxygen, IV Access, Labs" },
								position: { x: 0, y: 160 },
							},
							{
								id: "D",
								type: "decision-node",
								style: { width: 150, height: 150 },
								data: {
									label: "Hemodynamic Status?",
								},
								position: { x: 0, y: 240 },
							},
							{
								id: "U1",
								type: "default-node",
								data: { label: "Empiric Anticoagulation" },
								position: { x: -250, y: 360 },
							},
							{
								id: "U2",
								type: "default-node",
								data: { label: "CT Pulmonary Angiogram" },
								position: { x: -250, y: 440 },
							},
							{
								id: "S1",
								type: "default-node",
								data: { label: "Wells / Geneva Scoring" },
								position: { x: 250, y: 360 },
							},
							{
								id: "S2",
								type: "default-node",
								data: { label: "D-dimer if low–mod probability" },
								position: { x: 250, y: 440 },
							},
							{
								id: "E",
								type: "decision-node",
								style: { width: 150, height: 150 },
								data: {
									label: "Confirmed PE?",
								},
								position: { x: 250, y: 520 },
							},
							{
								id: "S3",
								type: "default-node",
								data: { label: "Thrombolysis / Embolectomy" },
								position: { x: -250, y: 520 },
							},
							{
								id: "T1",
								type: "default-node",
								data: { label: "Therapeutic Anticoagulation" },
								position: { x: 100, y: 640 },
							},
							{
								id: "X",
								type: "output-node",
								data: { label: "Consider Alternate Diagnosis" },
								position: { x: 400, y: 640 },
							},
							{
								id: "Z",
								type: "output-node",
								data: { label: "Admit, Monitor, Arrange Follow-Up" },
								position: { x: -120, y: 760 },
							},
						],
						correctEdges: [
							{
								id: "A-B",
								source: "A",
								target: "B",
							},
							{
								id: "B-C",
								source: "B",
								target: "C",
							},
							{
								id: "C-D",
								source: "C",
								target: "D",
							},
							{
								id: "D-U1",
								source: "D",
								sourceHandle: "positive",
								target: "U1",
								label: "Unstable",
								type: "custom",
							},
							{
								id: "D-S1",
								source: "D",
								sourceHandle: "negative",
								target: "S1",
								label: "Stable",
								type: "custom",
							},
							{
								id: "U1-U2",
								source: "U1",
								target: "U2",
							},
							{
								id: "U2-S3",
								source: "U2",
								target: "S3",
							},
							{
								id: "S3-Z",
								source: "S3",
								target: "Z",
							},
							{
								id: "S1-S2",
								source: "S1",
								target: "S2",
							},
							{
								id: "S2-E",
								source: "S2",
								target: "E",
							},
							{
								id: "E-T1",
								source: "E",
								sourceHandle: "positive",
								target: "T1",
								label: "Yes",
								type: "custom",
							},
							{
								id: "E-X",
								source: "E",
								sourceHandle: "negative",
								target: "X",
								label: "No",
								type: "custom",
							},
							{
								id: "T1-Z",
								source: "T1",
								target: "Z",
							},
						],
					},
				},
				score: 0,
			},
			{
				id: 7,
				versionId: 1,
				content: {
					parentId: 100,
					parentType: "SET",
					stem: "Based on the patient’s presentation and the imaging provided, identify the abnormality seen on this CT angiogram.",
					choices: [],
					instruction:
						"Precisely circle the area of CT image that you believe is abnormal.",
					media: [
						{
							id: "",
							url: "https://ajxjsnuynuxigljdrsyk.supabase.co/storage/v1/object/public/memorang-assets/demo/nbme/ct.jpeg",
							type: "image",
						},
					],
				},
				tags: [
					{
						id: 6480,
						type: "Skill",
						label: "Marking",
						breadcrumb: "Marking",
					},
				],
				response: {
					maximumScore: 10,
					subskillsSchema: [
						{
							name: "Abnormality Localization",
							icon: "target",
							maxScore: 5,
							description:
								"Evaluates the accuracy and precision of identifying and marking the correct abnormality on the image",
						},
						{
							name: "Avoidance of Irrelevant Marks",
							icon: "close-circle",
							maxScore: 5,
							description:
								"Assesses the student's ability to focus on the relevant finding without marking unrelated structures",
						},
					],
				},
				metadata: {
					type: "MARKING_ANSWER",
					skill: "MARKING",
					title: "Marking Answer",
					topics: [],
				},
				score: null,
			},
			{
				id: 8,
				type: "MCSS",
				content: {
					parentId: 100,
					parentType: "SET",
					stem: "After interpreting the CT angiogram and integrating the patient’s history and exam findings, what is the single best diagnosis? ",
					choices: [
						{
							id: "18773835-6d7a-4fe1-8310-d51dae72fc56",
							stem: "Community-acquired pneumonia",
							text: "Community-acquired pneumonia",
							media: [],
							isCorrect: false,
							explanation: null,
							numFirstSelections: 31,
						},
						{
							id: "c7f0d6c2-eb06-4439-bcb5-9c156285341a",
							stem: "Acute coronary syndrome",
							text: "Acute coronary syndrome",
							media: [],
							isCorrect: false,
							explanation: null,
							numFirstSelections: 35,
						},
						{
							id: "b1c04588-5729-4df3-b952-0ef334b0892b",
							stem: "Pulmonary embolism",
							text: "Pulmonary embolism",
							media: [],
							isCorrect: true,
							explanation:
								"The patient is a middle-aged man who developed sudden, sharp chest pain that worsens with deep breathing, along with shortness of breath. He has tachycardia, low oxygen levels, and a recent history of knee surgery with new swelling and tenderness in his left calf. His CT angiogram reveals a filling defect in the pulmonary artery.\n\nAll of these findings point strongly toward **pulmonary embolism**. The combination of sudden pleuritic chest pain, recent immobility from surgery, new leg symptoms, and a vascular filling defect on CT are classic features. This case shows how history, exam, and imaging work together to support pulmonary embolism as the most likely diagnosis.\n\n**Community-acquired pneumonia** would usually cause fever, cough, and a focal infiltrate on imaging—not just a vascular defect. While the patient has some respiratory symptoms, the lack of fever and the presence of clot risk factors make pneumonia unlikely here. **Acute coronary syndrome** could cause chest pain and shortness of breath, but the pain in ACS is usually described as pressure or heaviness, and may radiate to the arm or jaw. If the pain were not pleuritic or if cardiac risk factors were present, this diagnosis would be more likely, but the current findings do not support it. **Pericarditis** typically causes sharp chest pain that improves when sitting forward, and may show a pericardial rub or ECG changes—none of which are seen in this case.\n\n**Key Takeaways**\nIn a patient with acute pleuritic chest pain, shortness of breath, risk factors for thrombosis, and a matching imaging finding, **pulmonary embolism should be prioritized** in the differential diagnosis. Always match the history, exam, and imaging to arrive at the most likely cause.",
							numFirstSelections: 53,
						},
						{
							id: "b614cb5b-3d26-4d93-9213-19a9adbe3875",
							stem: "Pericarditis",
							text: "Pericarditis",
							media: [],
							isCorrect: false,
							explanation: null,
							numFirstSelections: 35,
						},
					],
					instruction: "Choose the single best answer",
					highlights: null,
					media: null,
				},
				tags: [
					{
						id: 6479,
						type: "Skill",
						label: "Reading",
						breadcrumb: "Reading",
					},
				],
				response: {
					maximumScore: 1,
					correctResponse: [
						{
							id: "b614cb5b-3d26-4d93-9213-19a9adbe3875",
						},
					],
					numChoicesRequired: {
						max: 1,
						min: 1,
					},
				},
				metadata: {
					type: "MCSS",
					skill: "READING",
					title: "Multiple Choice Single Selection",
					topics: ["Vocabulary"],
				},
				score: 0,
			},
			{
				id: 9,
				versionId: 1,
				content: {
					parentId: 100,
					parentType: "SET",
					stem: "You have now completed the full workup for this patient, including focused history, physical exam, and all relevant diagnostic studies. Integrate your findings to provide a clinical assessment and initial management plan.",
					choices: [],
					instruction:
						"Write your response in the fields below. Aim for 2-4 sentences per field.",
					media: null,
				},
				tags: [
					{
						id: 6480,
						type: "Skill",
						label: "Writing",
						breadcrumb: "Writing",
					},
				],
				response: {
					maximumScore: 25,
					goldStandard: {
						assessment:
							"Middle-aged patient with sudden-onset pleuritic chest pain, shortness of breath, tachycardia, hypoxia, recent knee surgery, and new left calf swelling found to have a pulmonary artery filling defect on CT—most consistent with acute pulmonary embolism. Determined to be hemodynamically unstable.",
						plan: [
							"Stabilize airway, breathing, and circulation",
							"Administer supplemental oxygen (target SpO2 >94%)",
							"Establish IV access and send CBC, CMP, coagulation panel, troponin, D-dimer",
							"Obtain 12-lead ECG and portable chest X-ray",
							"Order CT pulmonary angiography (CTPA)",
							"Begin therapeutic anticoagulation with IV unfractionated heparin once imaging is ordered",
							"If hemodynamically unstable, prepare for systemic thrombolysis or catheter/surgical embolectomy",
						],
						alerts: [
							"Hypoxia (SpO2 93%)",
							"Tachycardia (HR 110)",
							"Recent knee surgery",
							"Unilateral calf swelling",
							"High Wells score (probability 8)",
						],
					},
					subskillsSchema: [
						{
							name: "Synthesis of Key Findings",
							icon: "brain",
							description:
								"Clearly and concisely integrates all key findings, with logical connection to diagnosis",
							maxScore: 5,
						},
						{
							name: "Correct Diagnosis",
							icon: "check-circle",
							description:
								"Accurately states and prioritizes the most likely diagnosis, fully supported by the findings",
							maxScore: 5,
						},
						{
							name: "Initial Management Steps",
							icon: "clipboard-list",
							description:
								"All appropriate, prioritized steps listed in correct sequence; comprehensive and safe",
							maxScore: 5,
						},
						{
							name: "Escalation/Contingency",
							icon: "alert-circle",
							description:
								"Explicit, thorough plan for monitoring and escalation if instability or complications",
							maxScore: 5,
						},
						{
							name: "Clarity and Organization",
							icon: "format-list-bulleted",
							description:
								"Exceptionally clear, concise, and well-organized; excellent clinical language",
							maxScore: 5,
						},
					],
				},
				metadata: {
					type: "INTEGRATED_WRITING_STIMULUS",
					skill: "WRITING",
					title: "Integrated Writing Response",
					topics: [],
					sections: [
						{
							label: "Assessment",
							placeholder: "Write your assessment here",
						},
						{
							label: "Alerts",
							placeholder: "Write your alerts here",
						},
						{
							label: "Plan",
							placeholder: "Write your plan here",
						},
					],
				},
				score: null,
			},
			{
				id: 10,
				type: "AUDIO_INPUT_EMOTION",
				content: {
					parentId: 100,
					parentType: "SET",
					stem: `The patient is now recovering on the ward after successful treatment for a blood clot in the lung (pulmonary embolism). Following admission for sudden pleuritic chest pain, shortness of breath, and left calf swelling after recent knee surgery, diagnostic workup—including CT pulmonary angiogram—confirmed acute pulmonary embolism. Due to hemodynamic instability, thrombolytic therapy was administered in addition to anticoagulation (initially with heparin, then transitioned to an oral anticoagulant). The patient's respiratory status has since stabilized on room air. Serial monitoring has shown no new symptoms or complications. You are now rounding on the patient and they have some questions.`,
					choices: [],
					instruction: "Watch the video and then counsel the patient.",
					media: [
						{
							id: "vi6kZtC67NEp1tyVyepVObfQ",
							url: "vi6kZtC67NEp1tyVyepVObfQ",
							type: "video",
							transcript:
								"My chest hurts when I breathe in and I can’t catch my breath.",
						},
					],
				},
				tags: [
					{
						id: 6481,
						type: "Skill",
						label: "Speaking",
						breadcrumb: "Speaking",
					},
				],
				response: {
					maximumScore: 20,
					maximumDuration: 40,
					subskillsSchema: [
						{
							maxScore: 5,
							name: "Diagnosis & Treatment Explanation",
							icon: "stethoscope",
							description:
								"Provide a complete, accurate, and easily understandable explanation of both the diagnosis and treatment course. Use plain language, check for understanding, and address possible misconceptions or concerns.",
						},
						{
							maxScore: 5,
							name: "Discharge & Follow-up Instructions",
							icon: "clipboard-list",
							description:
								"Give specific, accurate, and comprehensive instructions for all aspects of discharge and follow-up care, including medications, lifestyle recommendations, lab monitoring, and follow-up appointments.",
						},
						{
							maxScore: 5,
							name: "Warning Signs",
							icon: "alert-circle",
							description:
								"Clearly identify and explain all critical warning signs and symptoms that should prompt immediate medical attention, explaining why they matter and ensuring the patient knows when and how to seek help.",
						},
						{
							maxScore: 5,
							name: "Patient Communication",
							icon: "message-outline",
							description:
								"Communicate in a patient-centered, clear, and empathetic manner. Maintain appropriate eye contact/tone, listen actively, use supportive language, and adjust explanations to the patient's level.",
						},
					],
				},
				metadata: {
					type: "AUDIO_INPUT_EMOTION",
					skill: "SPEAKING",
					title: "Audio Input Item",
					topics: ["LS Summary"],
				},
				score: 0,
			},
		],
	},
];
